import React from "react";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";

function StatCard({ label, value, unit, tone = "default", testId, hint }) {
  const tones = {
    default: "border-slate-100 bg-white",
    primary: "border-blue-100 bg-blue-50/40",
    accent: "border-indigo-100 bg-indigo-50/40",
    warning: "border-amber-200 bg-amber-50",
  };
  return (
    <div
      className={`pac-card pac-card-hover p-5 border ${tones[tone]}`}
      data-testid={testId}
    >
      <div className="pac-label uppercase tracking-wide text-[11px] text-slate-500">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="pac-value-xl">{value}</span>
        {unit && <span className="text-sm font-mono-data text-slate-500">{unit}</span>}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function ResultsPanel({ results }) {
  if (!results) {
    return (
      <div className="pac-card p-8 text-center text-slate-500" data-testid="results-empty">
        Remplissez le formulaire à gauche pour obtenir le dimensionnement.
      </div>
    );
  }

  // Plage de couverture (par défaut : 60–130 — compat. anciens projets)
  const minPct = results.couverture_min_pct ?? 60;
  const maxPct = results.couverture_max_pct ?? 130;
  const recoPct = results.couverture_pct ?? results.surpuissance_pct ?? maxPct;

  return (
    <div className="space-y-5" data-testid="results-panel">
      {/* Alert area */}
      {results.alerte_cycles_courts ? (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="alert-cycles-courts"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-display font-semibold text-amber-900">
              Surpuissance hors plage
            </div>
            <div className="text-sm text-amber-800 mt-1">
              La PAC recommandée représente <span className="font-mono-data font-semibold">{recoPct}%</span>{" "}
              des déperditions, soit plus du maximum autorisé de {maxPct}%. Risque de cycles courts.
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
          data-testid="alert-ok"
        >
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-emerald-900">
            PAC recommandée à <span className="font-mono-data font-semibold">{recoPct}%</span> des déperditions
            (plage acceptable {minPct}–{maxPct}%).
          </div>
        </div>
      )}

      {/* Hero result : plage de puissance */}
      <div className="pac-card p-5 sm:p-6 bg-grain relative overflow-hidden" data-testid="hero-result">
        <div className="absolute inset-y-0 right-0 w-1.5 bg-gradient-to-b from-blue-500 to-indigo-600" />
        <div className="pac-label uppercase tracking-wide text-[11px] text-slate-500">
          Puissance PAC nécessaire
        </div>
        <div className="mt-2 flex items-baseline gap-2 flex-wrap">
          <span className="font-mono-data text-2xl sm:text-3xl font-semibold text-slate-500" data-testid="hero-min">
            {results.puissance_min_kw}
          </span>
          <span className="text-slate-400 font-mono-data">→</span>
          <span className="font-mono-data text-4xl sm:text-5xl font-semibold text-slate-900" data-testid="hero-power">
            {results.puissance_totale_kw}
          </span>
          <span className="text-slate-400 font-mono-data">→</span>
          <span className="font-mono-data text-2xl sm:text-3xl font-semibold text-slate-500" data-testid="hero-max">
            {results.puissance_max_kw}
          </span>
          <span className="text-lg sm:text-xl font-mono-data text-slate-500 ml-1">kW</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3 text-xs">
          <div className="bg-slate-50 rounded-lg px-2 sm:px-3 py-2">
            <div className="text-slate-500 uppercase tracking-wider text-[9px] sm:text-[10px] font-medium">Min&nbsp;{minPct}%</div>
            <div className="font-mono-data font-semibold text-slate-900 mt-0.5">{results.puissance_min_kw} kW</div>
          </div>
          <div className="bg-blue-50 rounded-lg px-2 sm:px-3 py-2 border border-blue-100">
            <div className="text-blue-700 uppercase tracking-wider text-[9px] sm:text-[10px] font-medium">Reco&nbsp;{recoPct}%</div>
            <div className="font-mono-data font-semibold text-slate-900 mt-0.5">{results.puissance_totale_kw} kW</div>
          </div>
          <div className="bg-slate-50 rounded-lg px-2 sm:px-3 py-2">
            <div className="text-slate-500 uppercase tracking-wider text-[9px] sm:text-[10px] font-medium">Max&nbsp;{maxPct}%</div>
            <div className="font-mono-data font-semibold text-slate-900 mt-0.5">{results.puissance_max_kw} kW</div>
          </div>
        </div>
        <div className="mt-3 text-sm text-slate-600">
          Couverture recommandée : <span className="font-mono-data font-semibold text-slate-900">{results.couverture_pct ?? results.surpuissance_pct}%</span>{" "}
          des déperditions ({results.deperditions_kw} kW).
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          label="Volume chauffé"
          value={results.volume_m3}
          unit="m³"
          testId="stat-volume"
        />
        <StatCard
          label="ΔT (Tint − Text)"
          value={results.delta_t}
          unit="°C"
          testId="stat-delta-t"
          hint={`Text base ${results.t_base}°C`}
        />
        <StatCard
          label="Coefficient G"
          value={results.coef_g}
          unit="W/m³.°C"
          testId="stat-coef-g"
        />
        <StatCard
          label="Déperditions"
          value={results.deperditions_kw}
          unit="kW"
          tone="primary"
          testId="stat-deperditions"
        />
        <StatCard
          label="Couverture PAC"
          value={`${recoPct}`}
          unit="%"
          tone="primary"
          testId="stat-couverture"
          hint={`Plage ${minPct}–${maxPct}%`}
        />
        <StatCard
          label="Ballon tampon min."
          value={results.ballon_tampon_l}
          unit="L"
          tone="accent"
          testId="stat-ballon"
          hint="15 L par kW installé"
        />
      </div>

      <div className="pac-card p-5" data-testid="emetteur-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="pac-label uppercase tracking-wide text-[11px] text-slate-500">
              Émetteur de chauffage
            </div>
            <div className="mt-1 font-display text-lg font-semibold text-slate-900">
              {results.emetteur_label}
            </div>
          </div>
          <div className="text-right">
            <div className="pac-pill bg-blue-100 text-blue-800">
              Régime {results.emetteur_temp}
            </div>
          </div>
        </div>
      </div>

      {/* Synthèse table */}
      <div className="pac-card p-0 overflow-hidden" data-testid="synthese-table">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Info className="h-4 w-4 text-blue-600" />
          <span className="font-display font-semibold text-slate-900">Tableau de synthèse</span>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            <Row label="Volume V = Surface × Hauteur" value={`${results.volume_m3} m³`} />
            <Row label="Température extérieure de base" value={`${results.t_base} °C`} />
            <Row label="ΔT = Tint − Tbase" value={`${results.delta_t} °C`} />
            <Row label="Coefficient d'isolation G" value={`${results.coef_g}`} />
            <Row label="Déperditions = V × G × ΔT" value={`${results.deperditions_w.toLocaleString("fr-FR")} W`} highlight />
            <Row label={`Puissance min (${minPct}%)`} value={`${results.puissance_min_w.toLocaleString("fr-FR")} W (${results.puissance_min_kw} kW)`} />
            <Row label={`Puissance recommandée (${recoPct}%)`} value={`${results.puissance_totale_w.toLocaleString("fr-FR")} W (${results.puissance_totale_kw} kW)`} highlight />
            <Row label={`Puissance max (${maxPct}%)`} value={`${results.puissance_max_w.toLocaleString("fr-FR")} W (${results.puissance_max_kw} kW)`} />
            <Row label="Couverture recommandée" value={`${results.couverture_pct ?? results.surpuissance_pct} %`} />
            <Row label="Ballon tampon (15 L/kW)" value={`${results.ballon_tampon_l} L`} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <tr className={highlight ? "bg-blue-50/40" : ""}>
      <td className="px-4 sm:px-5 py-2.5 text-slate-700 text-sm">{label}</td>
      <td className="px-4 sm:px-5 py-2.5 text-right font-mono-data text-slate-900 font-medium text-sm whitespace-nowrap">
        {value}
      </td>
    </tr>
  );
}
